import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  fetchHomepageHeat, fetchOfficials, fetchStats,
  type HomepageHeatResponse, type Official, type StatsResponse,
} from './apiClient';
import OfficialAvatar from './src/components/OfficialAvatar';
import AdoptButton from './src/components/AdoptButton';
import HeatNowRail from './src/components/home/HeatNowRail';
import RecentReceiptsRail from './src/components/home/RecentReceiptsRail';
import { buildOfficialLifecycleSummary } from './officialListLifecycle';
import { buildFeaturedOfficialAction, chooseFeaturedOfficial, type FeaturedOfficialAction } from './homepageFeaturedAction';
import { SpineScoreGauge } from './src/components/SpineScore';
import { CaseFileHero } from './src/components/casefile/CaseFileHero';
import { featureFlags } from './featureFlags';

// ---------------------------------------------------------------------------
// Office hub descriptors (mirrors OfficeHubPage.tsx OFFICE_HUBS, sans emoji)
// ---------------------------------------------------------------------------

interface HomeOfficeHub {
  slug: string;
  title: string;
  officeName: string;
  tagline: string;
}

const HOME_OFFICE_HUBS: HomeOfficeHub[] = [
  { slug: 'governors', title: 'Governors', officeName: 'Governor', tagline: 'Chief executives of every U.S. state' },
  { slug: 'lieutenant-governors', title: 'Lieutenant Governors', officeName: 'Lieutenant Governor', tagline: 'Second-in-command of state government' },
  { slug: 'attorneys-general', title: 'Attorneys General', officeName: 'Attorney General', tagline: 'Top law enforcement officer in each state' },
  { slug: 'secretaries-of-state', title: 'Secretaries of State', officeName: 'Secretary of State', tagline: 'Guardians of elections and democracy' },
];

// ---------------------------------------------------------------------------
// HomePage
// ---------------------------------------------------------------------------

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [officialsPool, setOfficialsPool] = useState<Official[]>([]);
  const [leaders, setLeaders] = useState<Official[]>([]);
  const [homepageHeat, setHomepageHeat] = useState<HomepageHeatResponse | null>(null);
  const [totalAdoptions, setTotalAdoptions] = useState<number | null>(null);
  const [officeCounts, setOfficeCounts] = useState<Record<string, number>>({});
  const [referralLeaders, setReferralLeaders] = useState<{
    username: string;
    displayName: string;
    referredCount: number;
  }[]>([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');

  // Stats
  useEffect(() => {
    fetchStats().then(setStats).catch(() => {});
  }, []);

  // Officials pool (for featured action + spine leaderboards)
  useEffect(() => {
    fetchOfficials({ limit: 100 })
      .then((data) => setOfficialsPool(data.officials))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Adoption leaderboard + total adoptions sum
  useEffect(() => {
    fetch('/api/adoptions/leaderboard?limit=50')
      .then((r) => (r.ok ? r.json() : { leaders: [] }))
      .then((data: { leaders?: Official[] }) => {
        const l: Official[] = data.leaders || [];
        setLeaders(l);
        setTotalAdoptions(l.reduce((sum, o) => sum + (o.adoption_count ?? 0), 0));
      })
      .catch(() => {});
  }, []);

  // Referral leaderboard — top recruiters
  useEffect(() => {
    if (!featureFlags.homepageHeat) {
      setReferralLeaders([]);
      return;
    }

    fetch('/api/adoptions/referral-leaderboard?limit=10')
      .then((r) => (r.ok ? r.json() : { leaders: [] }))
      .then((data: { leaders?: { username: string; displayName: string; referredCount: number }[] }) => {
        setReferralLeaders((data.leaders || []).slice(0, 3));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!featureFlags.homepageHeat) {
      setHomepageHeat(null);
      return;
    }

    fetchHomepageHeat(3)
      .then(setHomepageHeat)
      .catch(() => setHomepageHeat(null));
  }, []);

  // Office hub counts — fetch state executive branch once and group by office_name
  useEffect(() => {
    fetchOfficials({ office_branch: 'executive', level: 'state', limit: 500 })
      .then((data) => {
        const counts: Record<string, number> = {};
        for (const o of data.officials) {
          const name = o.office_name?.trim();
          if (!name) continue;
          counts[name] = (counts[name] || 0) + 1;
        }
        setOfficeCounts(counts);
      })
      .catch(() => {});
  }, []);

  // Featured action — pick the strongest candidate from the pool, rotate
  const featuredActions = useMemo<FeaturedOfficialAction[]>(() => {
    const sorted = [...officialsPool]
      .filter((o) => o.accountability_score != null || o.adoption_count)
      .sort((a, b) => {
        const aScore = (a.adoption_count ?? 0) * 10 + (a.accountability_score ?? 0);
        const bScore = (b.adoption_count ?? 0) * 10 + (b.accountability_score ?? 0);
        return bScore - aScore;
      })
      .slice(0, 5);
    return sorted.map((o) => buildFeaturedOfficialAction(o)).filter(Boolean);
  }, [officialsPool]);

  const [featuredIndex, setFeaturedIndex] = useState(0);
  useEffect(() => {
    if (featuredActions.length <= 1) return;
    const timer = setInterval(() => {
      setFeaturedIndex((prev: number) => (prev + 1) % featuredActions.length);
    }, 8000);
    return () => clearInterval(timer);
  }, [featuredActions.length]);

  const featuredAction = featuredActions[featuredIndex] ?? null;

  // Spine leaderboards — sorted client-side (officials route has no score sort)
  const lowestSpine = useMemo(() => {
    return [...officialsPool]
      .filter((o) => o.accountability_score != null)
      .sort((a, b) => (a.accountability_score ?? 0) - (b.accountability_score ?? 0))
      .slice(0, 5);
  }, [officialsPool]);

  const highestSpine = useMemo(() => {
    return [...officialsPool]
      .filter((o) => o.accountability_score != null)
      .sort((a, b) => (b.accountability_score ?? 0) - (a.accountability_score ?? 0))
      .slice(0, 5);
  }, [officialsPool]);

  const topAdopted = useMemo(() => leaders.slice(0, 5), [leaders]);

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const term = searchTerm.trim();
      navigate(term ? `/officials?search=${encodeURIComponent(term)}` : '/officials');
    },
    [searchTerm, navigate]
  );

  return (
    <div className="container mx-auto px-4 py-10 sm:py-14">
      {/* ----------------------------------------------------------------- */}
      {/* HERO — search intake beside the featured dossier, so the hero's   */}
      {/* right half isn't dead space on wide screens.                     */}
      {/* ----------------------------------------------------------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6 mb-10 items-stretch">
        <CaseFileHero
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          onSubmit={handleSearchSubmit}
          totalOfficials={stats ? stats.total : null}
          totalAdoptions={totalAdoptions}
          dossiersCompleted={stats ? stats.dossiersCompleted : null}
        />
        {featuredAction && !loading && (
          <CompactFeaturedActionCard
            action={featuredAction}
            rotationDots={featuredActions.length}
            currentIdx={featuredIndex}
            onDotClick={setFeaturedIndex}
          />
        )}
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* LEDGER BAR — the one place these numbers appear, instead of       */}
      {/* repeating officials/dossiers/adoptions across hero + proof strip. */}
      {/* ----------------------------------------------------------------- */}
      <LedgerBar stats={stats} totalAdoptions={totalAdoptions} heat={homepageHeat} />

      {featureFlags.homepageHeat && homepageHeat && homepageHeat.items.length > 0 && (
        <>
          <HeatNowRail heat={homepageHeat} />
          <RecentReceiptsRail heat={homepageHeat} />
        </>
      )}

      {/* ----------------------------------------------------------------- */}
      {/* TABBED LEADERBOARD                                                */}
      {/* ----------------------------------------------------------------- */}
      <LeaderboardTabs
        topAdopted={topAdopted}
        lowestSpine={lowestSpine}
        highestSpine={highestSpine}
      />

      {/* ----------------------------------------------------------------- */}
      {/* TOP RECRUITERS                                                    */}
      {/* ----------------------------------------------------------------- */}
      {featureFlags.homepageHeat && referralLeaders.length > 0 && (
        <section className="max-w-4xl mx-auto mb-14">
          <div className="bg-cream-50 dark:bg-navy-800/50 border border-cream-300 dark:border-navy-600 rounded-xl p-5 sm:p-6">
            <h2 className="text-lg font-black text-navy-700 dark:text-cream-100 uppercase tracking-wide mb-1">
              Top Recruiters
            </h2>
            <p className="text-[10px] font-black uppercase tracking-widest text-gold-600 dark:text-gold-400 mb-5">
              Citizens growing the network
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {referralLeaders.map((recruiter, i) => (
                <div
                  key={recruiter.username}
                  className="flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-navy-700/40 border border-cream-200 dark:border-navy-700"
                >
                  <span className="text-lg font-black text-gold-600 dark:text-gold-400 w-6 text-center shrink-0">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-grow">
                    <p className="font-bold text-navy-700 dark:text-cream-200 text-sm truncate">
                      {recruiter.displayName}
                    </p>
                    <p className="text-[10px] font-black uppercase tracking-widest text-navy-400 dark:text-navy-300">
                      {recruiter.referredCount} Recruited
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ----------------------------------------------------------------- */}
      {/* FEDERAL / STATE QUICK NAV                                         */}
      {/* ----------------------------------------------------------------- */}
      <section className="max-w-6xl mx-auto mb-10">
        <h2 className="text-2xl sm:text-3xl font-black text-navy-900 dark:text-cream-50 uppercase tracking-tight mb-6 text-center">
          Track Every Level of Government
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Link
            to="/officials?level=federal"
            className="group relative overflow-hidden rounded-2xl border-2 border-amber-300 dark:border-amber-800/50 bg-gradient-to-br from-amber-50 to-white dark:from-amber-900/20 dark:to-navy-800/60 p-8 hover:border-amber-500 hover:shadow-xl transition"
          >
            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-amber-700 dark:text-amber-400 mb-2">United States</p>
            <h3 className="text-3xl font-black text-navy-900 dark:text-cream-50 uppercase tracking-tight mb-3 group-hover:text-amber-700 dark:group-hover:text-amber-400 transition">
              Federal Officials
            </h3>
            <p className="text-sm text-navy-500 dark:text-navy-300 mb-6">Congress, the executive branch, and every seat that answers to the whole country.</p>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-black text-amber-600 dark:text-amber-400">{stats ? stats.byLevel.find((l) => l.office_level === 'federal')?.count.toLocaleString() ?? 0 : '...'}</span>
              <span className="text-[10px] font-black uppercase tracking-widest text-navy-400 dark:text-navy-300">Tracked →</span>
            </div>
          </Link>
          <Link
            to="/officials?level=state"
            className="group relative overflow-hidden rounded-2xl border-2 border-teal-300 dark:border-teal-800/50 bg-gradient-to-br from-teal-50 to-white dark:from-teal-900/20 dark:to-navy-800/60 p-8 hover:border-teal-500 hover:shadow-xl transition"
          >
            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-teal-700 dark:text-teal-400 mb-2">All 50 States</p>
            <h3 className="text-3xl font-black text-navy-900 dark:text-cream-50 uppercase tracking-tight mb-3 group-hover:text-teal-700 dark:group-hover:text-teal-400 transition">
              State Officials
            </h3>
            <p className="text-sm text-navy-500 dark:text-navy-300 mb-6">Governors, state legislators, attorneys general, and secretaries of state.</p>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-black text-teal-600 dark:text-teal-400">{stats ? stats.byLevel.find((l) => l.office_level === 'state')?.count.toLocaleString() ?? 0 : '...'}</span>
              <span className="text-[10px] font-black uppercase tracking-widest text-navy-400 dark:text-navy-300">Tracked →</span>
            </div>
          </Link>
        </div>
      </section>

      {/* ----------------------------------------------------------------- */}
      {/* OFFICE HUB GRID                                                   */}
      {/* ----------------------------------------------------------------- */}
      <section className="max-w-6xl mx-auto mb-14">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-black text-navy-900 dark:text-cream-50 uppercase tracking-tight">
              Browse by Office After the Evidence
            </h2>
            <p className="text-[10px] font-black uppercase tracking-widest text-gold-600 dark:text-gold-400 mt-1">
              Taxonomy comes after movement, receipts, and live dossiers
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {HOME_OFFICE_HUBS.map((hub) => (
            <Link
              key={hub.slug}
              to={`/${hub.slug}`}
              className="group block bg-white dark:bg-navy-800/60 rounded-xl border-t-4 border-t-gold-500 border-x border-b border-cream-300 dark:border-navy-600 p-5 hover:-translate-y-1 hover:shadow-lg transition"
            >
              <h3 className="text-base font-black text-navy-900 dark:text-cream-50 uppercase tracking-tight group-hover:text-gold-600 dark:group-hover:text-gold-400 transition">
                {hub.title}
              </h3>
              <p className="text-[10px] text-navy-400 dark:text-navy-300 font-bold uppercase tracking-wider mt-1 mb-4 leading-relaxed">
                {hub.tagline}
              </p>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-black text-gold-600 dark:text-gold-400">
                  {officeCounts[hub.officeName] ?? '...'}
                </span>
                <span className="text-[10px] font-black uppercase tracking-widest text-navy-400 dark:text-navy-300">
                  Officials
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ----------------------------------------------------------------- */}
      {/* CLOSING CTA                                                       */}
      {/* ----------------------------------------------------------------- */}
      <section className="mb-14 flex flex-col sm:flex-row items-center justify-between gap-6 bg-navy-900 border-t-2 border-gold-500 px-6 sm:px-10 py-8">
        <div>
          <p className="text-2xl font-black text-cream-50 uppercase tracking-tight">Pick one. Watch them.</p>
          <p className="mt-2 text-sm text-cream-300/80">Free, takes 20 seconds, and you can drop them whenever they earn it back.</p>
        </div>
        <div className="flex gap-2.5 shrink-0">
          <Link to="/register" className="px-5 py-3 bg-gold-500 hover:bg-gold-400 text-navy-900 text-xs font-black uppercase tracking-widest transition">
            Create an Account
          </Link>
          <Link to="/officials" className="px-5 py-3 border-[1.5px] border-white/35 hover:bg-white/10 text-cream-50 text-xs font-black uppercase tracking-widest transition">
            Browse First
          </Link>
        </div>
      </section>

      {/* ----------------------------------------------------------------- */}
      {/* MANIFESTO + EMAIL CAPTURE                                         */}
      {/* ----------------------------------------------------------------- */}
      <ManifestoSection />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Ledger Bar — the single place officials/dossiers/signals/adoptions totals
// appear on the homepage, instead of repeating across the hero and a
// separate "mission proof" strip.
// ---------------------------------------------------------------------------

function countRecentSignals(items: HomepageHeatResponse['items'] = []): number {
  const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
  return items.filter((item) => item.eventTimestamp >= cutoff).length;
}

const LedgerBar: React.FC<{
  stats: StatsResponse | null;
  totalAdoptions: number | null;
  heat: HomepageHeatResponse | null;
}> = ({ stats, totalAdoptions, heat }) => {
  const cells = [
    { value: stats ? stats.total.toLocaleString() : '...', label: 'Officials on file' },
    { value: stats ? stats.dossiersCompleted.toLocaleString() : '...', label: 'Dossiers built' },
    { value: countRecentSignals(heat?.items ?? []).toLocaleString(), label: 'Signals this week' },
    { value: totalAdoptions !== null ? totalAdoptions.toLocaleString() : '...', label: totalAdoptions ? 'Adoptions' : 'Adoptions — board is open' },
  ];

  return (
    <section className="mb-14 grid grid-cols-2 sm:grid-cols-4 bg-navy-900 rounded-xl overflow-hidden">
      {cells.map((cell, i) => (
        <div key={cell.label} className={`px-5 py-4 ${i < cells.length - 1 ? 'border-r border-white/10' : ''} ${i < 2 ? 'sm:border-b-0 border-b border-white/10' : ''}`}>
          <p className={`text-2xl font-black ${i < 2 ? 'text-gold-400' : 'text-cream-50'}`}>{cell.value}</p>
          <p className="mt-1.5 text-[9px] font-black uppercase tracking-[0.13em] text-cream-300/70">{cell.label}</p>
        </div>
      ))}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Compact Featured Action Card
// ---------------------------------------------------------------------------

const CompactFeaturedActionCard: React.FC<{
  action: FeaturedOfficialAction;
  rotationDots?: number;
  currentIdx?: number;
  onDotClick?: (i: number) => void;
}> = ({ action, rotationDots, currentIdx, onDotClick }) => {
  const [shareState, setShareState] = useState<'idle' | 'copied' | 'error'>('idle');
  const { official, whyItMatters, shareText, shareTitle, shareUrlPath } = action;
  const lifecycle = buildOfficialLifecycleSummary(official);

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}${shareUrlPath}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
        setShareState('copied');
        return;
      }
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        setShareState('copied');
        return;
      }
      setShareState('error');
    } catch {
      setShareState('error');
    } finally {
      window.setTimeout(() => setShareState('idle'), 2200);
    }
  };

  return (
    <div className="rounded-xl border border-gold-500/30 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-cream-50 shadow-2xl overflow-hidden flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <span className="text-[9px] font-black uppercase tracking-[0.25em] text-gold-400">Featured · Live Dossier</span>
        {official.state && (
          <span className="text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full border border-white/25 text-cream-100">
            {official.state}
          </span>
        )}
      </div>

      <div className="flex gap-3.5 px-4 pt-4 pb-3">
        <OfficialAvatar
          name={official.name}
          photoUrl={official.photo_url}
          className="w-14 h-16 rounded bg-white/10 border border-white/10 flex items-center justify-center shrink-0"
          imgClassName="w-14 h-16 rounded object-cover border border-white/10"
          initialsClassName="text-base font-black text-cream-100 uppercase"
        />
        <div className="min-w-0">
          <p className="font-black text-lg leading-tight tracking-tight">{official.name}</p>
          <p className="mt-1 text-[11px] font-semibold text-cream-200/70 uppercase tracking-wide">
            {official.office_name}{official.state ? ` · ${official.state}` : ''}{official.party ? ` · ${official.party.slice(0, 3).toUpperCase()}` : ''}
          </p>
          {lifecycle.badgeLabel && (
            <span className="inline-block mt-1.5 text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-sky-500/20 border border-sky-400/20 text-sky-200">
              {lifecycle.badgeLabel}
            </span>
          )}
          <p className="mt-2 text-xs leading-relaxed text-cream-100/80">{whyItMatters}</p>
        </div>
      </div>

      <div className="flex items-center gap-4 px-4 pb-3">
        <SpineScoreGauge score={official.accountability_score ?? null} size={80} forceLight hideLabel />
        <div className="min-w-0">
          <p className="text-[9px] font-black uppercase tracking-[0.2em] text-cream-300/60">
            Spine score{official.accountability_score != null ? ` — ${official.accountability_score}/100` : ''}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-px bg-white/10 border-t border-white/10">
        <div className="bg-navy-900/40 px-4 py-2.5">
          <p className="text-[9px] font-black uppercase tracking-[0.14em] text-cream-300/60">Adoptions</p>
          <p className="mt-1 text-base font-black">{official.adoption_count ?? 0}</p>
        </div>
        <div className="bg-navy-900/40 px-4 py-2.5">
          <p className="text-[9px] font-black uppercase tracking-[0.14em] text-cream-300/60">Source</p>
          <p className="mt-1 text-sm font-black uppercase truncate">{official.source.replace(/_/g, ' ')}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2.5 px-4 py-3.5 mt-auto">
        <div className="min-w-0">
          <AdoptButton officialId={official.id} initialAdoptionCount={official.adoption_count ?? 0} />
        </div>
        <Link
          to={`/officials/${encodeURIComponent(official.id)}`}
          className="shrink-0 inline-flex items-center justify-center border border-white/30 hover:bg-white/10 text-cream-50 px-4 py-2.5 text-xs font-black uppercase tracking-widest transition"
        >
          View Dossier
        </Link>
        {featureFlags.receiptSharing && (
          <button
            onClick={handleShare}
            className="shrink-0 inline-flex items-center justify-center border border-white/15 bg-white/5 hover:bg-white/10 text-cream-50 px-3 text-xs font-black uppercase tracking-widest transition"
          >
            {shareState === 'copied' ? '✓' : shareState === 'error' ? '!' : 'Share'}
          </button>
        )}
      </div>

      {rotationDots && rotationDots > 1 && (
        <div className="flex items-center justify-center gap-2 pb-3.5">
          {Array.from({ length: rotationDots }).map((_, i) => (
            <button
              key={i}
              onClick={() => onDotClick?.(i)}
              className={`h-1.5 rounded-full transition-all ${i === currentIdx ? 'w-6 bg-gold-500' : 'w-1.5 bg-white/20 hover:bg-white/40'}`}
              aria-label={`Featured official ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};


// ---------------------------------------------------------------------------
// Tabbed Leaderboard
// ---------------------------------------------------------------------------

type LeaderboardTab = 'adopted' | 'lowest' | 'highest';

const LeaderboardTabs: React.FC<{
  topAdopted: Official[];
  lowestSpine: Official[];
  highestSpine: Official[];
}> = ({ topAdopted, lowestSpine, highestSpine }) => {
  const [tab, setTab] = useState<LeaderboardTab>('adopted');

  const tabs: { id: LeaderboardTab; label: string; data: Official[] }[] = [
    { id: 'adopted', label: 'Most Adopted', data: topAdopted },
    { id: 'lowest', label: 'Lowest Spine', data: lowestSpine },
    { id: 'highest', label: 'Highest Spine', data: highestSpine },
  ];

  const active = tabs.find((t) => t.id === tab) ?? tabs[0];
  const isZeroState = topAdopted.length === 0;
  // Zero-state falls back to the lowest-spine officials as suggested first
  // adoptions — an honest empty board still needs to point somewhere.
  const data = isZeroState ? lowestSpine.slice(0, 3) : active.data;

  return (
    <section className="max-w-6xl mx-auto mb-14">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 pb-3 border-b-2 border-navy-900 dark:border-cream-50">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gold-600 dark:text-gold-400">Section 02 — The Board</p>
          <h2 className="mt-2 text-2xl sm:text-3xl font-black text-navy-900 dark:text-cream-50 uppercase tracking-tight">
            {isZeroState ? 'Nobody has been adopted yet' : 'Most adopted right now'}
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-navy-500 dark:text-navy-300">
            {isZeroState
              ? 'Adopting an official means you get their receipts by email and your name goes on their dossier. These have the most open questions right now.'
              : 'These officials have the most citizens watching their record.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5 shrink-0">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-3.5 py-2 text-[10px] font-black uppercase tracking-widest transition border ${
                t.id === tab
                  ? 'bg-navy-900 dark:bg-gold-500 text-cream-50 dark:text-navy-900 border-navy-900 dark:border-gold-500'
                  : 'bg-white dark:bg-navy-800 text-navy-500 dark:text-cream-300 border-cream-300 dark:border-navy-600 hover:border-gold-500 hover:text-gold-600 dark:hover:text-gold-400'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {data.length === 0 ? (
        <p className="text-center text-[10px] font-black uppercase tracking-widest text-navy-400 dark:text-navy-300 py-10">
          No data available yet.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
          {data.map((official) => (
            <FirstMoverCard key={official.id} official={official} />
          ))}
        </div>
      )}

      <div className="mt-6 text-center">
        <Link
          to="/leaderboard"
          className="text-[10px] font-black uppercase tracking-widest text-gold-600 dark:text-gold-400 hover:underline"
        >
          View Full Leaderboard →
        </Link>
      </div>
    </section>
  );
};

const FirstMoverCard: React.FC<{ official: Official }> = ({ official }) => (
  <div className="flex gap-3 p-4 bg-white dark:bg-navy-800/60 border border-cream-300 dark:border-navy-600">
    <OfficialAvatar
      name={official.name}
      photoUrl={official.photo_url}
      className="w-12 h-14 shrink-0 rounded bg-cream-100 dark:bg-navy-700 border border-cream-300 dark:border-navy-600 flex items-center justify-center"
      imgClassName="w-12 h-14 rounded object-cover"
      initialsClassName="text-sm font-black text-navy-400 dark:text-cream-300 uppercase"
    />
    <div className="min-w-0 flex-1">
      <p className="font-black text-navy-900 dark:text-cream-50 truncate">{official.name}</p>
      <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wide text-navy-400 dark:text-navy-300 truncate">
        {official.office_name}{official.state ? ` · ${official.state}` : ''}
      </p>
      {official.accountability_score != null && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-base font-black text-navy-900 dark:text-cream-50">{official.accountability_score}</span>
        </div>
      )}
      <div className="mt-2.5 flex flex-wrap gap-1.5">
        <div className="min-w-0">
          <AdoptButton officialId={official.id} initialAdoptionCount={official.adoption_count ?? 0} />
        </div>
        <Link
          to={`/officials/${encodeURIComponent(official.id)}`}
          className="shrink-0 inline-flex items-center justify-center px-3 border border-cream-300 dark:border-navy-600 text-navy-700 dark:text-cream-200 text-[10px] font-black uppercase tracking-wide hover:border-gold-500 hover:text-gold-600 transition"
        >
          File
        </Link>
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Manifesto + Email Capture
// ---------------------------------------------------------------------------

const ManifestoSection: React.FC = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!featureFlags.newsletterSignup) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'homepage' }),
      });

      const data: any = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || 'Failed to subscribe');
      }

      setSubmitted(true);
      setEmail('');
    } catch (err: any) {
      setError(err?.message || 'Failed to subscribe');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="max-w-4xl mx-auto mb-8">
      <div className="rounded-2xl border border-gold-500/30 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-cream-50 p-8 sm:p-10 text-center shadow-xl">
        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gold-400 mb-4">
          Join the Movement
        </p>
        <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-tight leading-[0.95] mb-4">
          Accountability is a team sport.
        </h2>
        <p className="text-sm sm:text-base text-cream-200/90 max-w-2xl mx-auto mb-6 leading-relaxed">
          Every adoption is a citizen refusing to look away. We track the votes, the
          flip-flops, the donor money, and the silence. Then we put it in one place you
          can actually use. Sign up to get the spine reports that matter to you.
        </p>

        {submitted ? (
          <div className="max-w-md mx-auto rounded-xl border border-gold-500/40 bg-gold-500/10 px-5 py-4">
            <p className="text-sm font-black uppercase tracking-widest text-gold-400">
              You are on the list.
            </p>
            <p className="text-xs text-cream-200/80 mt-1">
              You will receive weekly live-data accountability digests.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="max-w-md mx-auto flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-grow px-4 py-3 rounded-lg bg-white/10 border border-white/15 text-cream-50 placeholder-cream-300/50 focus:border-gold-500 outline-none transition text-sm"
            />
            <button
              type="submit"
              disabled={submitting}
              className="bg-gold-500 hover:bg-gold-400 disabled:opacity-60 disabled:cursor-not-allowed text-navy-950 px-5 py-3 rounded-lg text-sm font-black uppercase tracking-widest transition"
            >
              {submitting ? 'Joining...' : 'Sign Up'}
            </button>
          </form>
        )}

        {error && (
          <p className="text-xs font-bold uppercase tracking-widest text-red-300 mt-4">
            {error}
          </p>
        )}

        <p className="text-[10px] font-black uppercase tracking-widest text-cream-300/60 mt-5">
          No spam. No fundraising. Just accountability.
        </p>
      </div>
    </section>
  );
};

export default HomePage;
